import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { PERSON_BY_ID_QUERY } from "../../../../../api/person/queries";
import { defaultPersonHistorikColumns } from "../../../../../config/person";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger,
} from "../../../../accordion/Accordion";
import ButtonDownload from "../../../../ButtonDownload";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import IngenRegistreringer from "../IngenRegistreringer";
import { PersonDto, Personhistorik } from "../../../../../interfaces/schema";
import { PersonHistorikField } from "../../../../../interfaces/personer";
import { IColumn, SortBy } from "../../../../../interfaces/table";
import useLocalStorageState from "use-local-storage-state";
import { orderBy } from "../../../../../util/orderBy";

export default function HistorikStamdata({
  personHistorik,
  personHistorikColumns,
  setPersonHistorikActiveColumns,
}: {
  personHistorik: Personhistorik[];
  personHistorikColumns: IColumn<PersonHistorikField>[];
  setPersonHistorikActiveColumns: React.Dispatch<
    React.SetStateAction<PersonHistorikField[]>
  >;
}) {
  const [sortBy, setSortBy] = useLocalStorageState<SortBy[]>(
    "personHistorikStamdataSortBy",
    {
      defaultValue: defaultSortBy,
    },
  );
  const params = useParams();
  const { id } = params;

  const { data, refetch } = useQuery<{
    person: PersonDto;
  }>(PERSON_BY_ID_QUERY(), {
    variables: { id },
  });

  // event handlers
  function downloadHandler() {
    tableToXLSX(personHistorikColumns, personHistorik, {
      fileName: `historik-for-${data?.person.kundeNr}`,
      additionalData: {
        kundenr: data?.person.kundeNr,
      },
    });
  }

  return (
    <>
      <AccordionItem
        className="c-card c-card--accordion"
        value="historikstamdata"
        isEmpty={!personHistorik.length}
      >
        <AccordionHeader>
          <AccordionTrigger>
            <span>Historik stamdata fra AMK</span>
          </AccordionTrigger>
        </AccordionHeader>
        <AccordionContent>
          <ButtonDownload
            onClick={downloadHandler}
            isDisabled={!personHistorik?.length}
          />
          {personHistorik.length > 0 ? (
            <ModalProvider>
              <TableRoot fixedHeight>
                <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
                  {getHeaderActiveColumns(personHistorikColumns, {
                    defaultSortBy,
                    setSortBy,
                    sortBy,
                  })}
                </TableHead>
                <TableBody>
                  {orderBy(
                    personHistorik,
                    sortBy.map((ds) => ds.field),
                    sortBy.map((ds) => ds.direction),
                  ).map((his) => {
                    return (
                      <TableRow key={his.id} invalidateData={refetch}>
                        {personHistorikColumns
                          .filter((ph) => ph.active)
                          .map((col) => {
                            return (
                              <TD key={col.field} col={col} rowData={his} />
                            );
                          })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableRoot>
              <ColumnEditorModal
                defaultColumns={defaultPersonHistorikColumns}
                columns={personHistorikColumns}
                setActiveColumns={setPersonHistorikActiveColumns}
              />
            </ModalProvider>
          ) : (
            <IngenRegistreringer invalidateData={refetch} />
          )}
        </AccordionContent>
      </AccordionItem>
    </>
  );
}

const defaultSortBy: SortBy<PersonHistorikField>[] = [
  {
    direction: "desc",
    field: "opretDato",
  },
];
